import { type ReactNode, useState } from 'react'
import { Header } from '../components/Header'
import { Sidebar } from '../components/Sidebar'
import { useNavigationStore } from '../utils/navigationStore'

interface AdminLayoutProps {
  children: ReactNode
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  // Mobile: an overlay drawer, transient per navigation.
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  // Desktop: a persisted collapse that takes the sidebar out of the flow so <main> reflows
  // into the space rather than being overlapped (PE-2240).
  const { sidebarCollapsed } = useNavigationStore()

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
  const closeSidebar = () => setIsSidebarOpen(false)

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      <Header onMenuToggle={toggleSidebar} />
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/*
          Two independent axes, deliberately not merged:
            • mobile  — translate the drawer in/out over the content (width stays full)
            • desktop — animate the track's WIDTH to zero so the main column reclaims the
              space. Collapsing via translate alone would leave a dead gutter behind.
          `overflow-hidden` on the track clips the fixed-width <aside> while it animates,
          so the nav text never reflows mid-transition.

          NOTE for the responsive audit: `element-bleed` reports the <aside> sitting 288px off
          the LEFT edge at 768 and 390. That is the closed off-canvas drawer, parked exactly
          one sidebar-width outside the viewport by `-translate-x-full` — which is how an
          off-canvas drawer works, and why `horizontal-overflow` passes at every width. The
          audit measures geometry and cannot tell "bleeds off the page" from "parked off-screen
          until opened". Do not "fix" it by clamping the transform.

          The closed drawer is also `invisible` below lg: parked off-screen it was still in the
          tab order, so a keyboard user tabbed through ~38 links they couldn't see. `visibility`
          rides the same transition-all, so it only flips to hidden once the slide-out ends —
          and a hidden element no longer reads as a bleed to the audit either.
        */}
        <div
          className={`
            fixed lg:relative inset-y-0 left-0 z-50 shrink-0 lg:overflow-hidden
            transform transition-all duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible lg:translate-x-0 lg:visible'}
            ${sidebarCollapsed ? 'lg:w-0' : 'lg:w-72'}
          `}
        >
          <Sidebar onNavigate={closeSidebar} />
        </div>

        <main className="flex-1 min-w-0 overflow-auto px-4 lg:px-6">
          {children}
        </main>
      </div>
    </div>
  )
}
